import i18n from '@/i18n';
import axios from 'axios';
import Swal from 'sweetalert2';

export function setupResponseInterceptor() {
  return function (response) {    
    if (response.data && response.data.error) {
      const errorMessage = response.data.error;
      const cleanedErrorMessage = errorMessage.replace(/\s?\(.*?\)$/, '').trim();
      
      const translationKey = `dbErrors.${cleanedErrorMessage}`;
      const translated = i18n.global.te(translationKey) ? i18n.global.t(translationKey) : cleanedErrorMessage;
      
      Swal.fire({
        icon: 'error',
        title: i18n.global.t('error'),
        text: translated,
        confirmButtonText: i18n.global.t('OK'),
        // footer: response.data.statusCode ? `Error Code: ${response.data.statusCode}` : null
      });

      const sessionId = sessionStorage.getItem('sid');
      
      if (sessionId && errorMessage) {
        if (errorMessage.includes('ERR_SESSION_NOT_FOUND') || 
            errorMessage.includes('UA-PWC') ||
            response.data.statusCode === 401) {
          
          let timerInterval;
          Swal.fire({
            title: errorMessage.includes('UA-PWC')
              ? i18n.global.t('password_changed_success')
              : i18n.global.t('session_terminated'),
            html: errorMessage.includes('UA-PWC')
              ? i18n.global.t('session_terminated_auto_logout')
              : i18n.global.t('session_terminated_detailed'),
            icon: errorMessage.includes('UA-PWC') ? 'success' : 'warning',
            showCancelButton: false,
            showConfirmButton: false,
            allowOutsideClick: false,
            timer: 10000,
            timerProgressBar: true,
            didOpen: () => {
              Swal.showLoading();
              const b = Swal.getHtmlContainer().querySelector('b');
              b.textContent = 10;
              timerInterval = setInterval(() => {
                b.textContent = (Swal.getTimerLeft() / 1000).toFixed(0);
              }, 1000);
            },
            willClose: () => {
              clearInterval(timerInterval);
              sessionStorage.clear();
              window.location.href = "/Login";
            }
          });
        }
      }
      return response;
    }
    
    return response;
  };
}

export function setupErrorInterceptor() {
  return function (error) {    
    if (!error.response) {
      Swal.fire({
        icon: 'error',
        title: i18n.global.t('error'),
        text: i18n.global.t('network_error') || 'Network error occurred',
        confirmButtonText: i18n.global.t('OK')
      });
      return Promise.reject(error);
    }
    
    const status = error.response.status;
    const errorData = error.response.data;
    
    if (errorData && errorData.error) {
      const errorMessage = errorData.error;
      const cleanedErrorMessage = errorMessage.replace(/\s?\(.*?\)$/, '').trim();
      
      const translationKey = `dbErrors.${cleanedErrorMessage}`;
      const translated = i18n.global.te(translationKey) ? i18n.global.t(translationKey) : cleanedErrorMessage;
      
      Swal.fire({
        icon: 'error',
        title: i18n.global.t('error'),
        text: translated,
        confirmButtonText: i18n.global.t('OK'),
        // footer: errorData.statusCode ? `Error Code: ${errorData.statusCode}` : `Error Code: ${status}`
      });

      // Session handling
      const sessionId = sessionStorage.getItem('sid');
      if (sessionId && errorMessage) {
        if (errorMessage.includes('ERR_SESSION_NOT_FOUND') || 
            errorMessage.includes('UA-PWC') ||
            status === 401) {
          
          let timerInterval;
          Swal.fire({
            title: errorMessage.includes('UA-PWC')
              ? i18n.global.t('password_changed_success')
              : i18n.global.t('session_terminated'),
            html: errorMessage.includes('UA-PWC')
              ? i18n.global.t('session_terminated_auto_logout')
              : i18n.global.t('session_terminated_detailed'),
            icon: errorMessage.includes('UA-PWC') ? 'success' : 'warning',
            showCancelButton: false,
            showConfirmButton: false,
            allowOutsideClick: false,
            timer: 10000,
            timerProgressBar: true,
            didOpen: () => {
              Swal.showLoading();
              const b = Swal.getHtmlContainer().querySelector('b');
              b.textContent = 10;
              timerInterval = setInterval(() => {
                b.textContent = (Swal.getTimerLeft() / 1000).toFixed(0);
              }, 1000);
            },
            willClose: () => {
              clearInterval(timerInterval);
              sessionStorage.clear();
              window.location.href = "/Login";
            }
          });
        }
      }
      
      return Promise.resolve({
        data: null,
        status: status,
        config: error.config,
        headers: error.response.headers,
        request: error.request,
        interceptorHandled: true
      });
    }
    
    let errorMessage = '';
    
    switch (status) {
      case 400:
        errorMessage = i18n.global.t('bad_request') || 'Bad request';
        break;
      case 401:
        errorMessage = i18n.global.t('unauthorized') || 'Unauthorized access';
        sessionStorage.clear();
        window.location.href = "/Login";
        return Promise.resolve({ 
          data: null, 
          status: status, 
          interceptorHandled: true 
        });
      case 403:
        errorMessage = i18n.global.t('forbidden') || 'Access forbidden';
        break;
      case 404:
        errorMessage = i18n.global.t('not_found') || 'Resource not found';
        break;
      case 409:
        errorMessage = i18n.global.t('conflict') || 'Data conflict occurred';
        break;
      case 422:
        errorMessage = i18n.global.t('validation_error') || 'Validation error';
        break;
      case 429:
        errorMessage = i18n.global.t('too_many_requests') || 'Too many requests';
        break;
      case 500:
        errorMessage = i18n.global.t('server_error') || 'Internal server error';
        break;
      case 502:
        errorMessage = i18n.global.t('bad_gateway') || 'Bad gateway';
        break;
      case 503:
        errorMessage = i18n.global.t('service_unavailable') || 'Service unavailable';
        break;
      default:
        errorMessage = i18n.global.t('unknown_error') || 'An unknown error occurred';
    }
    
    Swal.fire({
      icon: 'error',
      title: i18n.global.t('error'),
      text: errorMessage,
      confirmButtonText: i18n.global.t('OK'),
      // footer: `Error Code: ${status}`
    });
    
    return Promise.resolve({
      data: null,
      status: status,
      config: error.config,
      headers: error.response.headers,
      request: error.request,
      interceptorHandled: true
    });
  };
}

export function setupRequestInterceptor() {
  return function (config) {
    const sessionId = sessionStorage.getItem('sid');
    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }
    
    const language = localStorage.getItem('locale') || 'en';
    config.headers['Accept-Language'] = language;
    
    config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
    
    config.timeout = config.timeout || 30000;    
    return config;
  };
}

export function setupRequestErrorInterceptor() {
  return function (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Request Error:', error);
    }
    
    Swal.fire({
      icon: 'error',
      title: i18n.global.t('error'),
      text: i18n.global.t('request_failed') || 'Request failed to send',
      confirmButtonText: i18n.global.t('OK')
    });
    
    return Promise.reject(error);
  };
}

axios.interceptors.request.use(
  setupRequestInterceptor(),
  setupRequestErrorInterceptor()
);

axios.interceptors.response.use(
  setupResponseInterceptor(),
  setupErrorInterceptor()
);

export default axios;

export function clearUserSession() {
  sessionStorage.clear();
  localStorage.removeItem('user_data');
  window.location.href = "/Login";
}

export function setLanguage(lang) {
  localStorage.setItem('locale', lang);
}

export function isAuthenticated() {
  return !!sessionStorage.getItem('sid');
}

export function setRequestLogging(enabled) {
  if (process.env.NODE_ENV === 'development') {
    window.AXIOS_DEBUG = enabled;
  }
}